<script lang="ts">
  import DieBlank from '../../dice-tray/die-blank.svelte';
  import { createDie } from '../../models/die.type';
  import type { DieDefinition } from '../../models/die.type';
  import type { DieFace } from '../../models/face.type';
  import { glossary } from '../../../store';

  interface Props {
    // glossary.svelte clones each dieCollection entry and stamps the collection
    // key onto it as `title` before passing it in.
    data: DieDefinition & { title: string };
  }

  let { data }: Props = $props();

  /**
   * A definition holds ten faces ordered weakest to strongest. (They are written
   * the other way round in die.type.ts and reversed by parseDice() on load.)
   *
   * A die only ever shows six of the ten at a time: at level 0 that window is
   * faces 2-7, and Die's constructor slices `2 + level`, so each level of upgrade
   * slides the window one step towards the strong end -- the die drops its
   * weakest face and gains a stronger one in its place. -2 to +2 is the range,
   * which is what the two faces on either side of the base six are for.
   *
   * These two lookups replace a pair of switch statements that spelled the same
   * mapping out index by index.
   */
  const LEVELS = [-2, -1, 0, 0, 0, 0, 0, 0, 1, 2];
  const LABELS = ['-2', '-1', '1', '2', '3', '4', '5', '6', '+1', '+2'];

  type FaceEntry = { face: Array<DieFace>; label: string; level: number };

  const entries: Array<FaceEntry> = data.faces.map((face, index) => ({
    face,
    label: LABELS[index] ?? '',
    level: LEVELS[index] ?? 0,
  }));

  const bands = [
    { key: 'down', label: 'Downgraded', faces: entries.slice(0, 2) },
    { key: 'base', label: 'Base faces', faces: entries.slice(2, 8) },
    { key: 'up', label: 'Upgraded', faces: entries.slice(8) },
  ].filter((band) => band.faces.length);

  /**
   * Opens Techniques filtered to what this die can make at base level --
   * createDie defaults to level 0, so the recipes shown are the ones its six
   * standard faces can reach. Order matters: selecting a category clears the
   * search term, so the dice have to go in after it.
   */
  function openTechniques() {
    glossary.selected.set('techniques');
    glossary.searchTerm.set('');
    glossary.searchDie.set([createDie(data.title)]);
  }
</script>

<div class="container entry">
  <div class="heading">
    <h2>
      <span class="swatch" style="background: var(--{data.colour}, #ffffff)"></span>
      {data.title}
    </h2>

    <button class="techniques" onclick={openTechniques}>View Techniques</button>
  </div>

  <div class="bands">
    {#each bands as band (band.key)}
      <div class="band {band.key}">
        <span class="band-label">{band.label}</span>

        <div class="faces">
          {#each band.faces as entry (entry.label)}
            <div class="face">
              <DieBlank
                dieType={entry.face}
                colour={data.colour}
                sizeMod={1}
                solid={true}
                level={entry.level}
              ></DieBlank>
              <span class="face-label">{entry.label}</span>
            </div>
          {/each}
        </div>
      </div>
    {/each}
  </div>
</div>

<style lang="less">
  @import '../../../defines.less';

  .container {
    text-align: left;
  }

  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    grid-gap: 10px;
    margin-bottom: 10px;

    h2 {
      display: flex;
      align-items: center;
      grid-gap: 8px;
      font-size: 1.2rem;
      text-transform: capitalize;
    }
  }

  .swatch {
    // The face art is already tinted by this colour; the dot names it up front
    // so the list can be skimmed by colour.
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid rgba(0, 0, 0, 0.6);
    box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.5) inset;
  }

  .techniques {
    font-size: 0.75rem;
    padding: 6px 14px;
    background: var(--background-colour-dark);
    border: 1px solid var(--background-colour-light);
    box-shadow: none;
    transition:
      border-color 0.2s,
      color 0.2s;

    &::after {
      content: '\00a0\203A';
      display: inline-block;
      transition: transform 0.2s;
    }

    &:hover,
    &:focus-visible {
      filter: none;
      color: @edge-shine;
      border-color: @edge-warm;

      &::after {
        transform: translateX(3px);
      }
    }

    &:active {
      box-shadow: none;
    }
  }

  .bands {
    // Ten faces in a row overflows a phone, so the three bands wrap as units --
    // the six base faces stay together down to roughly a 320px column.
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    grid-gap: 10px;
  }

  .band {
    padding: 6px 10px 8px;
    border: 1px solid var(--background-colour-light);
    border-radius: 8px;

    // The window the die actually rolls, so it reads as the default and the two
    // side bands as what slides into it.
    &.base {
      background: var(--background-colour-dark);
    }

    &.down,
    &.up {
      border-style: dashed;
      opacity: 0.75;
    }
  }

  .band-label {
    display: block;
    margin-bottom: 4px;
    font-size: 0.6rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0.55;
  }

  .faces {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 4px;

    .face {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: relative;
    }

    .face-label {
      font-size: 0.65rem;
      opacity: 0.6;
    }
  }
</style>
